import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { Link, Route, useHistory, useRouteMatch } from "react-router-dom"

import Loader from "../../../components/Loader/Loader"
import Seo from "../../../components/Seo/Seo"
import { useData } from "../../../hooks/useData"
import Textarea from "../../../components/Form/Inputs/Textarea"
import { handleUpdateData } from "../../../store/data/actions"
import { iBasicQuery } from "../../../definitions/interfaces"
import { useImageUrl } from "../../../hooks/useImageUrl"
import { eImageVersions } from "../../../definitions/Image"
import { iUserBusiness } from "../../../definitions/User"

import s from "./BusinessProfile.less"
import { getBusinessRoutes } from "./routes"
import { FormProvider, useForm } from "react-hook-form"
import BusinessOverview from "./Overview"
import BusinessLocations from "./Locations"
import { store } from "react-notifications-component"
import { notificationOptions } from "../../../api/Definitions"
import DotButton from "../../../components/Buttons/DotButton"

interface iProps {
  profile: iUserBusiness
}

// interface iUserBusinessParams {
//   url?: string
//   title?: string
//   website?: string
//   description?: string
//   logo?: string
//   cover?: string
//   screenshots?: Array<string>
//   locations?: Array<string>
//   contacts?: Array<string>
//   phone?: string
//   email?: string
//   taxId?: string
//   companyRegistry?: string
//   currency?: string
//   imageNameUpdated?: boolean
// }

interface iUserBusinessParams {
  url?: string
  title?: string
  website?: string
}

const BusinessProfile: FunctionComponent = () => {
  const [isLoading, setLoading] = useState(false)
  const match = useRouteMatch<iUserBusinessParams>()

  const { data, loading, setQuery } = useData<
    iUserBusinessParams & iBasicQuery,
    iUserBusiness
  >({
    link: "/v2/user-business",
    shouldPingForData: false,
    shouldListenForHistory: false,
  })

  const history = useHistory()

  const item = useMemo(() => (data && data[0]) || {}, [data])

  const methods = useForm({ defaultValues: item, shouldUnregister: false })
  const {
    formState: { dirtyFields },
  } = methods

  useEffect(() => {
    methods.reset(item)
  }, [item])

  const onSubmit = (data: iUserBusiness) => {
    setLoading(true)
    handleUpdateData<iUserBusiness>({
      data,
      fields: dirtyFields as iUserBusiness,
      link: "/v1/user-business",
    })
      .then(() => {
        setQuery({})
        setLoading(false)
        store.addNotification({
          title: "Success!",
          message: "Exhibition successfully updated!",
          type: "success",
          ...notificationOptions,
        })
      })
      .catch((e) => {
        if (e) {
          if (e.reason === "Nothing changed!") {
            store.addNotification({
              message: e.reason,
              type: "warning",
              ...notificationOptions,
            })
          } else {
            store.addNotification({
              message: e.reason,
              type: "danger",
              ...notificationOptions,
            })
          }
        } else {
          store.addNotification({
            message: "Something went wrong!",
            type: "danger",
            ...notificationOptions,
          })
        }
        setLoading(false)
      })
  }

  const coverImage = useImageUrl(eImageVersions.XLWRegular, item.coverObject)

  const navRoutes = getBusinessRoutes({ urlPrefix: match.url })

  const invokeSubmit = () => {
    methods.handleSubmit((data) => {
      onSubmit(data as iUserBusiness)
    })()
  }

  console.log(dirtyFields)

  return (
    <>
      <Seo title={"Company Profile"} />
      <div className="inspector-header">
        <div className="inspector-cover-wrap">
          <div
            className="inspector-cover"
            style={{ backgroundImage: `url(${coverImage})` }}
          ></div>
          <div className="inspector-cover-overlay">
            <div className="inspector-headline">
              <div className="justify-self-center">
                {/* logo */}
                <h1>{item.title}</h1>
              </div>
              <DotButton
                className={`btn-blue submit-button align-self-start`}
                text="Update"
                type="button"
                isLoading={isLoading}
                onClick={invokeSubmit}
              />
            </div>
          </div>
        </div>
        <div className="inspector-navigation">
          <ul>
            {navRoutes.map((n, i) => {
              return (
                <li
                  key={i}
                  className={
                    history.location.pathname === n.route
                      ? "active"
                      : Object.keys(dirtyFields).find((k) =>
                          n.fields?.includes(k),
                        )
                      ? "dirty"
                      : ""
                  }
                >
                  <Link to={n.route} replace>
                    <span>{n.title}</span>
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      </div>
      <div className={`inspector ${s.inspector}`}>
        {loading || isLoading ? (
          <Loader />
        ) : (
          <FormProvider {...methods}>
            <form
              className="inspector-form scrollable"
              onSubmit={methods.handleSubmit<iUserBusiness>((data) => {
                onSubmit(data as iUserBusiness)
              })}
            >
              <Route exact path={match.path}>
                <BusinessOverview item={item} />
              </Route>
              <Route exact path={match.path + "/locations"}>
                <BusinessLocations item={item} />
              </Route>
              <Route exact path={match.path + "/contacts"}>
                <ManageBusinessContacts profile={item} />
              </Route>
              <Route exact path={match.path + "/about"}>
                <ManageBusinessAbout profile={item} />
              </Route>
              <Route exact path={match.path + "/user-access"}>
                <ManageBusinessUserAccess profile={item} />
              </Route>
              <button className="d-none" type="submit">
                Submit
              </button>
            </form>
          </FormProvider>
        )}
      </div>
    </>
  )
}

export const ManageBusinessContacts: FunctionComponent<iProps> = () => {
  return (
    <div>
      <button>Add Location</button>
    </div>
  )
}

export const ManageBusinessAbout: FunctionComponent<iProps> = () => {
  return (
    <div>
      <Textarea name="description" label="Description" />
      <button>Add screenshot</button>
    </div>
  )
}

export const ManageBusinessUserAccess: FunctionComponent<iProps> = () => {
  return <div>User Access</div>
}

export default BusinessProfile
